import { cva, type VariantProps } from 'class-variance-authority';
import { createElement, type ElementType, type Ref } from 'react';

import { cn } from '@/utils/cn';

export const typographyVariants = cva('', {
  variants: {
    variant: {
      h1: 'font-sans font-semibold text-[48px] leading-[48px] tracking-[-1.5px]',
      h2: 'font-sans font-semibold text-[30px] leading-[30px] tracking-[-1px]',
      h3: 'font-sans font-semibold text-[24px] leading-[28.8px] tracking-[-1px]',
      h4: 'font-sans font-semibold text-[20px] leading-[24px] tracking-[0]',
      monospaced:
        'font-mono font-normal text-[16px] leading-[24px] tracking-[0]',
      paragraph:
        'font-sans font-normal text-[16px] leading-[24px] tracking-[0]',
      paragraphMedium:
        'font-sans font-medium text-[16px] leading-[24px] tracking-[0]',
      paragraphBold:
        'font-sans font-semibold text-[16px] leading-[24px] tracking-[0]',
      paragraphSmall:
        'font-sans font-normal text-[14px] leading-[20px] tracking-[0]',
      paragraphSmallMedium:
        'font-sans font-medium text-[14px] leading-[20px] tracking-[0]',
      paragraphSmallBold:
        'font-sans font-semibold text-[14px] leading-[20px] tracking-[0]',
      paragraphMini:
        'font-sans font-normal text-[12px] leading-[16px] tracking-[0]',
      paragraphMiniMedium:
        'font-sans font-medium text-[12px] leading-[16px] tracking-[0]',
      paragraphMiniBold:
        'font-sans font-semibold text-[12px] leading-[16px] tracking-[0]',
      paragraphMicro:
        'font-sans font-normal text-[10px] leading-[14px] tracking-[0]',
      paragraphMicroMedium:
        'font-sans font-medium text-[10px] leading-[14px] tracking-[0]',
    },
    color: {
      foreground: 'text-foreground',
      muted: 'text-muted-foreground',
      primary: 'text-primary',
      secondary: 'text-secondary-foreground',
      destructive: 'text-destructive',
      success: 'text-green-500',
      warning: 'text-amber-500',
      info: 'text-blue-500',
    },
  },
});

const VARIANT_TO_TAG = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  monospaced: 'p',
  paragraph: 'p',
  paragraphMedium: 'p',
  paragraphBold: 'p',
  paragraphSmall: 'p',
  paragraphSmallMedium: 'p',
  paragraphSmallBold: 'p',
  paragraphMini: 'p',
  paragraphMiniMedium: 'p',
  paragraphMiniBold: 'p',
  paragraphMicro: 'p',
  paragraphMicroMedium: 'p',
} as const;

const VARIANT_TO_DEFAULT_COLOR = {
  h1: 'foreground',
  h2: 'foreground',
  h3: 'foreground',
  h4: 'foreground',
  monospaced: 'muted',
  paragraph: 'muted',
  paragraphMedium: 'muted',
  paragraphBold: 'muted',
  paragraphSmall: 'muted',
  paragraphSmallMedium: 'muted',
  paragraphSmallBold: 'muted',
  paragraphMini: 'muted',
  paragraphMiniMedium: 'muted',
  paragraphMiniBold: 'muted',
  paragraphMicro: 'muted',
  paragraphMicroMedium: 'muted',
} as const;

export type TypographyVariant = keyof typeof VARIANT_TO_TAG;
export type TypographyColor = NonNullable<
  VariantProps<typeof typographyVariants>['color']
>;

export type TypographyProps = Omit<
  React.HTMLAttributes<HTMLElement>,
  'color'
> & {
  variant: TypographyVariant;
  color?: TypographyColor;
  /**
   * Override the rendered HTML tag without changing the visual variant.
   * Use to keep a correct heading hierarchy (e.g. `variant='h3' as='h1'`)
   * while preserving the design-system typography scale.
   */
  as?: ElementType;
  ref?: Ref<HTMLElement>;
};

/**
 * React 19: `ref` is accepted as a normal prop — no `forwardRef` needed.
 */
export const Typography = ({
  variant,
  color,
  as,
  className,
  children,
  ref,
  ...rest
}: TypographyProps) => {
  const tag = as ?? VARIANT_TO_TAG[variant];
  const resolvedColor = color ?? VARIANT_TO_DEFAULT_COLOR[variant];
  return createElement(
    tag,
    {
      ref,
      className: cn(
        typographyVariants({ variant, color: resolvedColor }),
        className,
      ),
      ...rest,
    },
    children,
  );
};
